import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { DropdownStyles } from "../../types/styles";
import Icon, { IconType } from "../Icon/Icon";
import { ChevronDownSolid, ChevronUpSolid } from "../../assets/icons";

const DropdownContainer = styled.div`
  display: flex;
  gap: 5px;
  flex-direction: column;
  width: 100%;
  font-size: 13px;
`;

const StyledLabel = styled.label<{
  styles: DropdownStyles;
}>`
  color: ${({ styles }) => styles.OverallGeneralTextColour};
  font-weight: 700;
`;

const SelectContainer = styled.div<{
  styles: DropdownStyles;
  isErrored?: boolean;
  leftBorderRadiusOnly?: boolean;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  position: relative;
  height: 40px;
  box-sizing: border-box;
  padding: 0 12px;
  background: ${({ styles }) => styles.DropdownGeneralBackgroundColour};
  color: ${({ styles }) => styles.DropdownGeneralTextColour};
  border: ${({ styles, isErrored }) =>
    `1px solid ${isErrored ? styles.DropdownGeneralValidationErrorColour : styles.DropdownBorderColour}`};
  ${({ leftBorderRadiusOnly }) => leftBorderRadiusOnly && `border-right: none;`}
  border-radius: ${({ leftBorderRadiusOnly, styles }) =>
    leftBorderRadiusOnly
      ? `${styles.DropdownBorderRadius}px 0 0 ${styles.DropdownBorderRadius}px`
      : `${styles.DropdownBorderRadius}px`};
  cursor: pointer;
  gap: 10px;

  &:focus {
    outline: ${({ styles }) => `1px solid ${styles.DropdownBorderFocusColour}`};
    border: ${({ styles }) => `1px solid ${styles.DropdownBorderFocusColour}`};
  }
`;

const SelectedValue = styled.div<{
  styles: DropdownStyles;
  selected: boolean;
}>`
  font-size: 13px;
  width: 100%;
  display: flex;
  align-items: center;
  color: ${({ styles, selected }) =>
    selected
      ? styles.DropdownGeneralTextColour
      : styles.DropdownGeneralPlaceholderColour};
`;

const LeftIcon = styled(Icon)`
  font-size: 16px;
`;

const ChevronIcon = styled(Icon)`
  font-size: 16px;
`;

const DropdownMenu = styled.div<{
  isOpen: boolean;
  styles: DropdownStyles;
  width?: string;
}>`
  display: ${({ isOpen }) => (isOpen ? "block" : "none")};
  position: absolute;
  width: ${({ width }) => width ?? "100%"};
  top: 103%;
  left: 0;
  background: ${({ styles }) => styles.DropdownGeneralBackgroundColour};
  border: 1px solid #828d98;
  max-height: 200px;
  overflow-y: auto;
  z-index: 2;
`;

const StyledOption = styled.div<{
  isSelected: boolean;
  styles: DropdownStyles;
  matchesSearch: boolean;
  searchActive: boolean;
}>`
  height: 35px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  background: ${({ isSelected, styles, matchesSearch, searchActive }) => {
    if (matchesSearch) return styles.DropdownSelectedBackgroundColour;
    if (isSelected && !searchActive)
      return styles.DropdownSelectedBackgroundColour;
    return "transparent";
  }};
  color: ${({ isSelected, styles, matchesSearch, searchActive }) => {
    if (matchesSearch) return styles.DropdownSelectedTextColour;
    if (isSelected && !searchActive) return styles.DropdownSelectedTextColour;
    return styles.DropdownGeneralTextColour;
  }};
  cursor: pointer;

  &:hover {
    background: ${({ styles }) => styles.DropdownSelectedBackgroundColour};
    color: ${({ styles }) => styles.DropdownSelectedTextColour};
  }
`;

const OptionLeftText = styled.div`
  margin-right: 6px;
  font-weight: 700;
`;

const ErrorMessage = styled.div<{
  styles: DropdownStyles;
}>`
  color: ${({ styles }) => styles.DropdownGeneralValidationErrorColour};
`;

export type DropdownOptions = {
  label: string | number;
  value: string | number;
  /**
   * Additional text to display on the left side of the option.
   */
  leftText?: string;
}[];

export type DropdownProps = {
  options: DropdownOptions;
  selected?: string | number;
  onSelect: (option: string | number) => void;
  label?: string;
  /**
   * Should be passed in if there is no label.
   */
  ariaLabel?: string;
  placeholder?: string;
  icon?: IconType;
  styles: DropdownStyles;
  /**
   * If passed in, the error message will be displayed and the border
   * will change.
   */
  errorMessage?: string;
  /**
   * Should be set to true if the dropdown is errored but error message
   * should not be displayed (in DateOfBirthSelector and MobileInput).
   */
  isErrored?: boolean;
  menuWidth?: string;
  /**
   * If true, the dropdown will only have a border radius on the left side.
   * This is used for the MobileInput component.
   */
  leftBorderRadiusOnly?: boolean;
  dataId?: string;
};

export const Dropdown: React.FC<DropdownProps> = ({
  options,
  selected,
  onSelect,
  label,
  ariaLabel,
  placeholder,
  icon,
  styles,
  errorMessage,
  isErrored,
  menuWidth,
  leftBorderRadiusOnly,
  dataId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [matchedOption, setMatchedOption] = useState<{
    label: string | number;
    value: string | number;
    leftText?: string;
  } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedOptionRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);

  const handleSelect = (value: string | number) => {
    onSelect(value);
    setIsOpen(false);
    setMatchedOption(null);
  };

  const selectedOption = options.find(
    (option) => option.value.toString() === selected?.toString()
  );

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
      setMatchedOption(null);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && selectedOptionRef.current && dropdownMenuRef.current) {
      const dropdownMenu = dropdownMenuRef.current;
      const optionElement = selectedOptionRef.current;

      const dropdownMenuRect = dropdownMenu.getBoundingClientRect();
      const optionElementRect = optionElement.getBoundingClientRect();

      const isOptionVisible =
        optionElementRect.top >= dropdownMenuRect.top &&
        optionElementRect.bottom <= dropdownMenuRect.bottom;

      // Scroll the dropdown menu to bring the selected option into view
      if (!isOptionVisible) {
        const scrollOffset =
          optionElement.offsetTop - dropdownMenu.clientHeight / 2;
        dropdownMenu.scrollTo({ top: scrollOffset, behavior: "auto" });
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchTerm) {
      const matchedOption = options.find(
        (option) =>
          typeof option.label === "string" &&
          option.label.toLowerCase().startsWith(searchTerm.toLowerCase())
      );
      if (matchedOption) {
        setMatchedOption(matchedOption);
        const matchedOptionElement = document.getElementById(
          `dropdown-option-${matchedOption.value}`
        );
        // Scroll the dropdown menu to bring the matched option into view
        if (matchedOptionElement && dropdownMenuRef.current) {
          const dropdownMenu = dropdownMenuRef.current;
          const optionElement = matchedOptionElement as HTMLElement;
          const dropdownMenuHeight = dropdownMenu.clientHeight;
          const optionElementHeight = optionElement.clientHeight;
          const optionElementOffsetTop = optionElement.offsetTop;
          const scrollPosition =
            optionElementOffsetTop -
            dropdownMenuHeight / 2 +
            optionElementHeight / 2;
          dropdownMenu.scrollTo({ top: scrollPosition, behavior: "auto" });
        }
      }
    }
  }, [searchTerm, options]);

  const handleClick = () => {
    setIsOpen(!isOpen);
    setMatchedOption(null);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      if (matchedOption !== null) {
        handleSelect(matchedOption.value);
      } else {
        setIsOpen(!isOpen);
      }
    } else if (event.key === "Escape") {
      setIsOpen(false);
    } else if (event.key.length === 1 && event.key.match(/[a-z0-9]/i)) {
      setSearchTerm((prev) => prev + event.key);
      // Clear the search term after 1.5 seconds
      setTimeout(() => setSearchTerm(""), 1500);
    }
  };

  return (
    <DropdownContainer ref={dropdownRef} data-id={dataId}>
      {label && (
        <StyledLabel htmlFor={label} styles={styles} data-id="dropdown-label">
          {label}
        </StyledLabel>
      )}
      <SelectContainer
        id={label}
        role="combobox"
        aria-label={label ?? ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-owns={`dropdown-listbox-${label}`}
        aria-controls={`dropdown-listbox-${label}`}
        aria-activedescendant={
          selected ? `dropdown-option-${selected}` : undefined
        }
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        styles={styles}
        isErrored={isErrored || !!errorMessage}
        leftBorderRadiusOnly={leftBorderRadiusOnly}
        data-id="dropdown-select-container"
      >
        {icon && <LeftIcon icon={icon} data-id="dropdown-left-icon" />}
        <SelectedValue
          styles={styles}
          selected={!!selectedOption}
          data-id="dropdown-selected-value"
        >
          {selectedOption ? selectedOption.value : placeholder}
        </SelectedValue>
        <ChevronIcon
          icon={isOpen ? ChevronUpSolid : ChevronDownSolid}
          data-id="dropdown-chevron-icon"
        />
        <DropdownMenu
          id={`dropdown-listbox-${label}`}
          role="listbox"
          isOpen={isOpen}
          styles={styles}
          width={menuWidth}
          ref={dropdownMenuRef}
          data-id="dropdown-menu"
        >
          {options.map((option) => (
            <StyledOption
              id={`dropdown-option-${option.value}`}
              role="option"
              key={option.value}
              aria-selected={selected === option.value}
              tabIndex={0}
              isSelected={selected === option.value}
              onClick={() => handleSelect(option.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") handleSelect(option.value);
              }}
              styles={styles}
              ref={selected === option.value ? selectedOptionRef : null}
              matchesSearch={option.label === matchedOption?.label}
              searchActive={!!matchedOption}
              data-id={`dropdown-option-${option.value}`}
            >
              {option.leftText && (
                <OptionLeftText data-id="dropdown-left-text">
                  {option.leftText}
                </OptionLeftText>
              )}
              {option.label}
            </StyledOption>
          ))}
        </DropdownMenu>
      </SelectContainer>
      {errorMessage && (
        <ErrorMessage styles={styles} data-id="dropdown-error">
          {errorMessage}
        </ErrorMessage>
      )}
    </DropdownContainer>
  );
};

export default Dropdown;
